"use client"

import { JSX, useEffect, useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import { useAuth } from "@/hooks/useAuth"
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import LoadingSpinner from "@/components/shared/LoadingSpinner"
import {
  getAdminWeeklyTokenUsage,
  type TokenUsageResponse,
} from "@/services/resourceUsageService"
import TokenUsageCharts from "./TokenUsageCharts"
import { colorForAgent, formatAgentLabel } from "./tokenUsageUtils"

const PAGE_TITLE = "Overall Token Usage"

// Formats an exact token count with thousands separators.
const numberFormatter = new Intl.NumberFormat()

/**
 * AdminTokenUsagePage component.
 *
 * Manager-only view of token usage aggregated across all users for the last
 * seven days.
 * Access is enforced server-side (the endpoint returns 403 for non-managers);
 * on any load failure the user is returned to the personal token usage page.
 *
 * @returns The rendered admin Token Usage interface.
 */
export default function AdminTokenUsagePage(): JSX.Element {
  const navigate = useNavigate()
  const { getAccessToken } = useAuth()

  const [usage, setUsage] = useState<TokenUsageResponse | null>(null)
  const [isLoading, setIsLoading] = useState<boolean>(true)

  useEffect(() => {
    let cancelled = false

    async function fetchUsage(): Promise<void> {
      setIsLoading(true)
      try {
        const timeZone: string =
          Intl.DateTimeFormat().resolvedOptions().timeZone
        const accessToken = await getAccessToken()
        const data = await getAdminWeeklyTokenUsage(timeZone, accessToken)
        if (!cancelled) {
          setUsage(data)
        }
      } catch (err) {
        // A failure here is most likely a 403 for a non-manager. Rather than
        // showing an error, return the user to their personal page
        console.error("Failed to load admin token usage:", err)
        if (!cancelled) {
          navigate("/computing/resource-usage/token", { replace: true })
        }
        return
      } finally {
        if (!cancelled) {
          setIsLoading(false)
        }
      }
    }

    fetchUsage()

    return () => {
      cancelled = true
    }
  }, [getAccessToken, navigate])

  // Whether there is any non-zero usage across the whole window.
  const hasAnyUsage: boolean = useMemo(() => {
    if (!usage) {
      return false
    }
    return usage.usage.some((day) =>
      Object.values(day.agents).some((counts) => counts.totalTokens > 0)
    )
  }, [usage])

  return (
    <>
      <title>{PAGE_TITLE} - MAP Apps Platform</title>
      <Card className="flex flex-1 flex-col min-h-0 shadow-none gap-4">
        <CardHeader className="pb-0">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>
                <h1>{PAGE_TITLE}</h1>
              </CardTitle>
              <CardDescription>
                <p className="text-sm text-muted-foreground mt-1">
                  Token usage across all users over the last seven days.
                </p>
              </CardDescription>
            </div>
            {/* Navigate back to the personal token usage view. */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate("/computing/resource-usage/token")}
            >
              My Usage
            </Button>
          </div>
        </CardHeader>
        <CardContent className="flex flex-1 flex-col min-h-0 gap-8 overflow-auto">
          {isLoading && (
            <div className="flex flex-1 items-center justify-center">
              <LoadingSpinner loadingContent="Loading token usage..." />
            </div>
          )}

          {!isLoading && usage && !hasAnyUsage && (
            <div className="flex flex-1 flex-col items-center justify-center gap-2">
              <p className="text-lg font-semibold">No Usage Yet</p>
              <p className="text-sm text-muted-foreground">
                No token usage recorded across any users in the last seven days.
              </p>
            </div>
          )}

          {!isLoading && usage && hasAnyUsage && (
            <>
              <TokenUsageCharts usage={usage} />
              <TopUsersSection usage={usage} />
            </>
          )}
        </CardContent>
      </Card>
    </>
  )
}

/**
 * TopUsersSection renders, for each agent, the top consuming users over the
 * window as a ranked list.
 *
 * Reads `usage.topUsers` (populated only by the admin endpoint). Agents with no
 * ranked users are skipped.
 *
 * @param usage - The admin token usage response (must include topUsers).
 * @returns The top-users section, or null when there is nothing to show.
 */
function TopUsersSection({
  usage,
}: {
  usage: TokenUsageResponse
}): JSX.Element | null {
  const topUsers = usage.topUsers
  if (!topUsers) {
    return null
  }

  // Only show agents that actually have ranked users.
  const agentsWithUsers = usage.agents.filter(
    (agent) => (topUsers[agent]?.length ?? 0) > 0
  )

  if (agentsWithUsers.length === 0) {
    return null
  }

  return (
    <section className="flex flex-col gap-3">
      <h2 className="text-sm font-medium">Top users by agent</h2>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {agentsWithUsers.map((agent) => (
          <div key={agent} className="rounded-md border p-3">
            <div className="mb-2 flex items-center gap-2">
              <span
                className="inline-block size-3 rounded-sm"
                style={{ backgroundColor: colorForAgent(agent) }}
                aria-hidden="true"
              />
              <h3 className="text-sm font-medium">{formatAgentLabel(agent)}</h3>
            </div>
            <ol className="flex flex-col gap-1">
              {topUsers[agent].map((entry, index) => (
                <li
                  key={entry.user}
                  className="flex items-center justify-between gap-3 text-xs"
                >
                  <span
                    className="min-w-0 truncate text-muted-foreground"
                    title={entry.name}
                  >
                    {index + 1}. {entry.name}
                  </span>
                  <span className="tabular-nums whitespace-nowrap">
                    {numberFormatter.format(entry.totalTokens)}
                  </span>
                </li>
              ))}
            </ol>
          </div>
        ))}
      </div>
    </section>
  )
}
